// Locale layout route. Owns the providers (i18n + auth) for everything under
// /:locale, then gates: splash while loading, the sign-in landing when
// unauthenticated, the role-aware shell when authed.

import { I18nProvider } from '@fajrarabi/i18n/react';
import { createFileRoute, Outlet, useParams, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppSplash } from '~/components/AppSplash';
import { ConsentBanner } from '~/components/ConsentBanner';
import { ImpersonationBanner } from '~/components/ImpersonationBanner';
import { InstallPrompt } from '~/components/InstallPrompt';
import { RoleAwareShell } from '~/components/RoleAwareShell';
import { SignInLanding } from '~/components/SignInLanding';
import { track } from '~/lib/analytics';
import { API_BASE } from '~/lib/api';
import { AuthProvider, useAuth } from '~/lib/auth';
import { asLocale } from '~/lib/locale';

export const Route = createFileRoute('/$locale')({
  component: LocaleLayout,
});

function LocaleLayout() {
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);

  // The URL segment is authoritative here; the resolution chain (cookie /
  // localStorage) still governs which locale a bare visit lands on.
  return (
    <I18nProvider apiBase={API_BASE} locale={locale}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </I18nProvider>
  );
}

// Routes that render standalone (no shell chrome) even when signed out.
const PUBLIC_CHILD_PATHS = ['/signup', '/school-signup', '/teacher-invite'];

// First-party page_view on every navigation (always-on product telemetry —
// R8, never consent-gated; see packages/db schema/analytics.ts).
function PageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    track('page_view', { path: pathname });
    if (pathname.endsWith('/signup')) track('start_signup', { path: pathname });
  }, [pathname]);
  return null;
}

function Gate() {
  const { loading, unauthenticated } = useAuth();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicChild = PUBLIC_CHILD_PATHS.some((p) => path.endsWith(p));

  if (loading) return <AppSplash />;

  if (unauthenticated) {
    // Public child routes (signup) render themselves; everything else shows
    // the sign-in landing.
    return (
      <>
        <PageViewTracker />
        {isPublicChild ? <Outlet /> : <SignInLanding />}
        <ConsentBanner />
      </>
    );
  }

  return (
    <>
      <PageViewTracker />
      <ImpersonationBanner />
      <RoleAwareShell>
        <Outlet />
        <InstallPrompt />
      </RoleAwareShell>
      <ConsentBanner />
    </>
  );
}
